/* eslint-disable react-hooks/exhaustive-deps */
import {
  Close,
  CopyAll,
  FilterAltOff,
  PublishedWithChanges,
} from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CustomSelect,
  CustomTextField,
  DebouncedTextField,
} from "../../../components/formsComponents";
import { StandardTableActions } from "../../../components/StandardTableActions/StandardTableActions";
import { DataTableV2 } from "../../../components/TableV2/DataTableV2";
import {
  IDataTableV2State,
  useTableV2State,
} from "../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../components/TableV2/interfaces/IDataTableV2Props";
import { useDebounce, useDispatchWrapper } from "../../../hooks";
import { TABLE_CACHING_KEYS, STANDARD_APP_DATE_FORMAT } from "../../../constants/constants";
import EditIcon from "@mui/icons-material/Edit";
import { useBillingCompanyContext } from "../../../providers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import {
  MarketCampaignStatusRenderer,
} from "../../../components/StatusRenderer/MarketCampaignStatusRenderer";
import { DataTableV2Date } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date";
import { getActiveFilterSx } from "../../../helpers/customeFilters";
import produce from "immer";
import { getDataTablev2InitialDate } from "../../../components/TableV2/helpers/dataTableV2DatesFilter";
import { DataTableV2MobileFilter } from "../../../components/TableV2/components/mobileDataTableV2CommandBar/MobileFilter";
import moment from "moment";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";
import { useTableData } from "../../../hooks/useTableData";
import { ViewMarketingCampaignRightPanel } from "../../marketingCampaign/components/ViewMarketingRighPanel";
import { MarketCampaignAnalyticsRightPanel } from "../../market-campaign/components/MarketCampaignAnalyticsRightPanel";
import {
  cloneCampaignAsync,
  runQuickCampaign,
} from "../../../redux/marketCampaign/marketCampaignActions";
import { showMessage } from "../../../redux/messages/messagesActions";

interface IPaymentReminderCampaign {
  market_campaign_uuid: string;
  campaign_name: string;
  status: string;
  total_audience: number;
  sent_messages: number;
  delivered_messages: number;
  read_messages: number;
  failed_messages: number;
  sending_type: string;
  campaign_start_date: string;
  campaign_end_date: string;
  create_ts: string;
}

export const PaymentReminderCampaignTable = () => {
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const { ftDateTime, ftDate } = useTimezoneFormatter();
  const companyOutlet = useBillingCompanyContext();

  const [openView, setOpenView] = React.useState<IPaymentReminderCampaign | null>(null);
  const [openAnalytics, setOpenAnalytics] = React.useState<IPaymentReminderCampaign | null>(null);

  const handleCopyClick = (rowData: IPaymentReminderCampaign) => {
    dispatch(
      cloneCampaignAsync(
        companyOutlet.comapny.company_outlet_uuid,
        rowData.market_campaign_uuid,
        (isSuccess) => {
          if (isSuccess) {
            refetch();
          }
        },
      ),
    );
  };

  const handleReRunCampaignClick = (rowData: IPaymentReminderCampaign) => {
    dispatch(
      runQuickCampaign(rowData.market_campaign_uuid, (isSuccess) => {
        if (isSuccess) {
          setTimeout(() => {
            refetch();
          }, 1000);
        }
      }),
    );
  };

  const {
    state,
    setDateState,
    setSelectedTab,
    resetState,
    setState,
    setSearchState,
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
    onResizeMasterColumns,
    initialColumnVisibility,
  } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last30Days",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
    cachingFilters: {
      uniqueKey: TABLE_CACHING_KEYS.PAYMENT_REMINDER_CAMPAIGN_LIST,
    },
    otherCustomStateProperties: {
      searchByName: "",
      selectedTab: "-1",
    },
  });

  const {
    dateState,
    searchState,
    tabs,
    columnsConfig,
    pagination,
    masterColumnResize,
    otherCustomStateProperties,
  } = state;

  const debouncedSearchName = useDebounce(
    otherCustomStateProperties["searchByName"],
    500,
  );

  const { data: list, loading, totalRecords, refetch } = useTableData<IPaymentReminderCampaign>({
    path: "/campaign/get-campaigns",
    params: {
      page: pagination.pageNumber,
      pageLimit: pagination.rowsInPerPage,
      status: tabs.selectedTab !== "-1" ? tabs.selectedTab : undefined,
      fromDate: dateState.dates.fromDate || undefined,
      toDate: dateState.dates.toDate || undefined,
      search: debouncedSearchName || undefined,
      company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      // Extra Custom Filter
      automation_name: "PAYMENT_REMINDER"
    },
  });

  const setFilterStateSync = React.useCallback(
    (value: React.SetStateAction<IDataTableV2State>) => {
      setState((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        const isSameDateState =
          next.dateState?.rangeType === "allTimes" &&
          !next.dateState?.dates?.fromDate &&
          !next.dateState?.dates?.toDate;
        if (isSameDateState) return next;
        return {
          ...next,
          dateState: {
            rangeType: "allTimes",
            dates: { fromDate: "", toDate: "" },
          },
        };
      });
    },
    [setState],
  );

  const handleCustomFilterChange = (key: string, value: any) => {
    const newState = produce(state, (draftState) => {
      draftState.otherCustomStateProperties[key] = value;
    });
    setFilterStateSync(newState);
  };

  const tableProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: pagination.rowsInPerPage,
    isDataLoading: loading,
    extraFetchFactor: pagination.extraFetchFactor,
    totalRecords: totalRecords,
    pageNumber: pagination.pageNumber,
    tableCommandBarProps: {
      customCommandBar: (
        <Stack direction="column" spacing={1} width="100%">
          <Stack direction="row" width="100%" spacing={1}>
            <Box>
              <DataTableV2Date
                state={dateState}
                onChange={setDateState}
                isHighlighted={dateState.rangeType !== "last30Days"}
              />
            </Box>

            <FormControl sx={{ minWidth: "150px" }} size="small">
              <InputLabel>Select Status</InputLabel>
              <CustomSelect
                label="Select Status"
                value={tabs.selectedTab}
                onChange={(e) => setSelectedTab(e.target.value as string)}
                displayEmpty
                sx={getActiveFilterSx(tabs.selectedTab !== "-1")}
              >
                <MenuItem value="-1">All</MenuItem>
                <MenuItem value="READY_TO_EXECUTE">Ready to execute</MenuItem>
                <MenuItem value="DONE">Done</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="RUNNING">Running</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </CustomSelect>
            </FormControl>

            <CustomTextField
              variant="outlined"
              label="Search by Name"
              placeholder="Search by Name"
              value={otherCustomStateProperties.searchByName}
              onChange={(e) => handleCustomFilterChange("searchByName", e.target.value)}
              sx={getActiveFilterSx(!!otherCustomStateProperties.searchByName)}
              InputProps={{
                endAdornment: otherCustomStateProperties.searchByName && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleCustomFilterChange("searchByName", "")}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip title="Clear Filters">
              <IconButton
                onClick={() => {
                  resetState();
                  setState((prevState) =>
                    produce(prevState, (draft) => {
                      draft.dateState.rangeType = "last30Days";
                      draft.dateState.dates = { ...getDataTablev2InitialDate("last30Days") };
                      draft.tabs.selectedTab = "-1";
                      draft.otherCustomStateProperties.searchByName = "";
                      draft.pagination.pageNumber = 1;
                    }),
                  );
                }}
                size="small"
                sx={{
                  height: "90%",
                  borderRadius: "10%",
                  border: "1px solid #e0e0e0",
                  backgroundColor:
                    otherCustomStateProperties.searchByName || tabs.selectedTab !== "-1" || dateState.rangeType !== "last30Days"
                      ? "warning.main"
                      : "transparent",
                }}
              >
                <FilterAltOff />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      ),
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          initialColumnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: () => refetch(),
        },
      },
      leftItems: { customPlugins: [] },
      rightItems: { customPlugins: [] },
    },

    mobileCommandBarProps: {
      filterComponent: (
        <DataTableV2MobileFilter
          state={state}
          onApply={(newState) => setState(newState)}
          onClear={resetState}
        >
          {(filterLocalState, setFilterlocalState) => (
            <Stack direction="column" spacing={2}>
              <Box>
                <DataTableV2Date
                  state={filterLocalState.dateState}
                  onChange={(newDateState) => setFilterlocalState({ ...filterLocalState, dateState: newDateState })}
                  isHighlighted={filterLocalState.dateState.rangeType !== "last30Days"}
                />
              </Box>
              <Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Status</InputLabel>
                  <CustomSelect
                    label="Select Status"
                    value={filterLocalState.tabs.selectedTab}
                    onChange={(e) => setFilterlocalState({ ...filterLocalState, tabs: { ...filterLocalState.tabs, selectedTab: e.target.value as string } })}
                    displayEmpty
                  >
                    <MenuItem value="-1">All</MenuItem>
                    <MenuItem value="READY_TO_EXECUTE">Ready to execute</MenuItem>
                    <MenuItem value="DONE">Done</MenuItem>
                    <MenuItem value="DRAFT">Draft</MenuItem>
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="RUNNING">Running</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </CustomSelect>
                </FormControl>
              </Box>
              <Box>
                <DebouncedTextField
                  label="Search by Name"
                  fullWidth
                  value={filterLocalState.otherCustomStateProperties.searchByName}
                  onChange={(val) => setFilterlocalState({ ...filterLocalState, otherCustomStateProperties: { ...filterLocalState.otherCustomStateProperties, searchByName: val } })}
                  delay={600}
                />
              </Box>
            </Stack>
          )}
        </DataTableV2MobileFilter>
      ),
    },

    mobileColumns: [
      {
        variant: "template1",
        headingColumn: { key: "campaign_name", fieldName: "campaign_name" },
        rightHeadingColumn: {
          key: "status",
          fieldName: "status",
          onRowCellRender: (value, row: IPaymentReminderCampaign) => (
            <MarketCampaignStatusRenderer status={row.status || ""} />
          ),
        },
        otherColumns: [
          {
            key: "campaign_start_date",
            headerName: "Start Date",
            fieldName: "campaign_start_date",
            onRowCellRender: (value, row: IPaymentReminderCampaign) => ftDate(row.campaign_start_date || ""),
          },
          {
            key: "campaign_end_date",
            headerName: "End Date",
            fieldName: "campaign_end_date",
            onRowCellRender: (value, row: IPaymentReminderCampaign) => ftDate(row.campaign_end_date || ""),
          },
          {
            key: "create_ts",
            headerName: "Created At",
            fieldName: "create_ts",
            onRowCellRender: (value, row: IPaymentReminderCampaign) => ftDateTime(row.create_ts || ""),
          },
        ],
        actions: [
          {
            name: "Edit",
            icon: <EditIcon />,
            click: (rowData: IPaymentReminderCampaign) => navigate("/payment-reminder-campaign/manage/" + rowData.market_campaign_uuid),
          },
          {
            name: "View",
            icon: <VisibilityIcon />,
            click: (rowData: IPaymentReminderCampaign) => setOpenView(rowData),
          },
          {
            name: "Analytics",
            icon: <EqualizerIcon />,
            click: (rowData: IPaymentReminderCampaign) => setOpenAnalytics(rowData),
          },
        ],
      },
    ],

    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        isFirstColumnSticky: true,
        minWidth: 70,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => (
          <Box sx={{ display: "flex", gap: 2 }}>
            <StandardTableActions
              status={row.status}
              onViewClick={() => setOpenView(row)}
              // onEditClick={() => navigate(`/payment-reminder-campaign/manage/${row.market_campaign_uuid}`)}
              onAnalyticsClick={() => setOpenAnalytics(row)}
              // otherActions={[
              //   {
              //     icon: CopyAll,
              //     tooltip: "Clone Campaign",
              //     onClick: () => handleCopyClick(row),
              //   },
              //   {
              //     icon: PublishedWithChanges,
              //     tooltip: "Re-Run Campaign",
              //     hide:
              //       row.status !== "FAILED" &&
              //       !(
              //         row.campaign_start_date &&
              //         moment(row.campaign_start_date).isValid() &&
              //         moment(row.campaign_start_date).isBefore(
              //           moment().subtract(1, "days"),
              //         )
              //       ),
              //     onClick: () => handleReRunCampaignClick(row),
              //   },
              // ]}
            />
          </Box>
        ),
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => (
          <MarketCampaignStatusRenderer status={row.status || ""} />
        ),
      },
      {
        key: "campaign_name",
        headerName: "Campaign Name",
        fieldName: "campaign_name",
        renderType: DataTableV2RowRenderType.TEXT,
        enableSorting: true,
      },
      {
        key: "total_audience",
        headerName: "Total",
        fieldName: "total_audience",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => (
          <Typography>{row.total_audience || 0}</Typography>
        ),
      },
      {
        key: "total_sent",
        headerName: "Total Sent",
        fieldName: "total_sent",
        enableSorting: false,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => {
          const totalSent = (row.sent_messages || 0) + (row.delivered_messages || 0);
          return <Typography>{totalSent}</Typography>;
        },
      },
      {
        key: "failed_messages",
        headerName: "Failed",
        fieldName: "failed_messages",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => (
          <Typography>{row.failed_messages || 0}</Typography>
        ),
      },
      {
        key: "sending_type",
        headerName: "Sending Type",
        fieldName: "sending_type",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "campaign_start_date",
        headerName: "Campaign Start Date",
        fieldName: "campaign_start_date",
        renderType: DataTableV2RowRenderType.DATE,
        enableSorting: true,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => {
          if (row.campaign_start_date) {
            const finalDate = ftDate(row.campaign_start_date || "");
            const hasMatched = finalDate === moment().format(STANDARD_APP_DATE_FORMAT);
            return <Typography sx={{ color: hasMatched ? "error.main" : "" }}>{finalDate}</Typography>;
          }
          return "--";
        },
      },
      {
        key: "campaign_end_date",
        headerName: "Campaign End Date",
        fieldName: "campaign_end_date",
        renderType: DataTableV2RowRenderType.DATE,
        enableSorting: true,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => {
          if (row.campaign_end_date) {
            const finalDate = ftDate(row.campaign_end_date || "");
            const hasMatched = finalDate === moment().format(STANDARD_APP_DATE_FORMAT);
            return <Typography sx={{ color: hasMatched ? "error.main" : "" }}>{finalDate}</Typography>;
          }
          return "--";
        },
      },
      {
        key: "create_ts",
        headerName: "Created At",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
        onRowCellRender: (value, row: IPaymentReminderCampaign) => ftDateTime(row.create_ts || ""),
      },
    ],

    rows: list,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
    masterColumnResize: {
      values: masterColumnResize,
      onResize: onResizeMasterColumns,
    },
  };

  return (
    <>
      <DataTableV2 {...tableProps} />

      {openView && (
        <ViewMarketingCampaignRightPanel
          open={true}
          data={openView as any}
          onClose={() => setOpenView(null)}
        />
      )}

      {openAnalytics && (
        <MarketCampaignAnalyticsRightPanel
          open={true}
          campaignData={openAnalytics as any}
          onClose={() => setOpenAnalytics(null)}
        />
      )}
    </>
  );
};
